import { useState, type FormEvent } from 'react';
import type {
  AuditRecord,
  DomainView,
  PlatformHealth,
  TenantDetail,
  TenantSummary,
} from 'zitimail-shared-types';

import { TenantAiPanel } from './ai';
import { api, describeError } from './api';
import { DlpPanel } from './dlp';
import { EDiscoveryPanel } from './ediscovery';
import {
  AliasesPanel,
  DirectorySyncPanel,
  AppPasswordsPolicyPanel,
  AppSignInPolicyPanel,
  ArchiveMovePolicyPanel,
  TwoStepPolicyPanel,
  PasswordHelpPanel,
  DisclaimerPanel,
  ForwardingPolicyPanel,
  MailboxesPanel,
  ScimPanel,
  IdentityProviderPanel,
  TenantSettingsPanel,
  size,
} from './mailboxes';
import { MigrationsPanel } from './migrations';
import { RetentionPanel } from './retention';
import { href } from './router';
import { ActionButton, Badge, CopyButton, Loaded, statusTone, useLoad, when } from './ui';

// ─── Overview ────────────────────────────────────────────────────────────────

export function Overview() {
  const [health, reloadHealth] = useLoad(
    (signal) => api<PlatformHealth>('/platform/health', { signal }),
    'health',
  );
  const [tenants] = useLoad(
    (signal) => api<{ tenants: TenantSummary[] }>('/platform/tenants', { signal }),
    'tenants',
  );
  return (
    <>
      <div className="adm-head">
        <h2>Platform overview</h2>
        <button type="button" className="btn ghost" onClick={reloadHealth}>
          Refresh
        </button>
      </div>
      <Loaded load={tenants}>
        {({ tenants: list }) => {
          const count = (status: string) => list.filter((t) => t.status === status).length;
          return (
            <div className="adm-stats">
              <Stat label="Active tenants" value={count('active')} />
              <Stat label="Suspended" value={count('suspended')} />
              <Stat label="Mailboxes" value={list.reduce((n, t) => n + t.mailboxes, 0)} />
              <Stat
                label="Verified domains"
                value={`${list.reduce((n, t) => n + t.verified_domains, 0)} / ${list.reduce((n, t) => n + t.domains, 0)}`}
              />
            </div>
          );
        }}
      </Loaded>
      <section className="adm-panel">
        <h3>Health</h3>
        <Loaded load={health}>
          {(h) => (
            <ul className="adm-health">
              {h.components.map((c) => (
                <li key={c.name}>
                  <Badge tone={c.ok ? 'ok' : 'crit'}>{c.ok ? 'OK' : 'Attention'}</Badge>
                  <strong>{COMPONENT_LABELS[c.name] ?? c.name}</strong>
                  <span className="adm-muted">{c.detail}</span>
                </li>
              ))}
            </ul>
          )}
        </Loaded>
        <p className="adm-muted adm-small">
          Per-server detail and maintenance: <a href={href({ page: 'nodes' })}>Nodes</a>.
        </p>
      </section>
    </>
  );
}

const COMPONENT_LABELS: Record<string, string> = {
  database: 'Database',
  keystore: 'DKIM keystore',
  keycloak: 'Sign-in (Keycloak)',
  tenant_dns: 'Tenant DNS',
  nodes: 'Nodes',
};

function Stat({ label, value }: { label: string; value: number | string }) {
  return (
    <div className="adm-stat">
      <span className="adm-stat-value">{value}</span>
      <span className="adm-muted">{label}</span>
    </div>
  );
}

// ─── Tenants ─────────────────────────────────────────────────────────────────

export function Tenants() {
  const [load, reload] = useLoad(
    (signal) => api<{ tenants: TenantSummary[] }>('/platform/tenants', { signal }),
    'tenants',
  );
  return (
    <>
      <div className="adm-head">
        <h2>Tenants</h2>
      </div>
      <NewTenant onCreated={reload} />
      <section className="adm-panel">
        <Loaded load={load}>
          {({ tenants }) =>
            tenants.length === 0 ? (
              <p className="adm-muted">No tenants yet.</p>
            ) : (
              <table className="adm-table">
                <thead>
                  <tr>
                    <th>Tenant</th>
                    <th>Status</th>
                    <th>Plan</th>
                    <th>Domains</th>
                    <th>Seats</th>
                    <th>Created</th>
                  </tr>
                </thead>
                <tbody>
                  {tenants.map((t) => (
                    <tr key={t.id}>
                      <td>
                        <a href={href({ page: 'tenant', slug: t.slug })}>{t.name}</a>
                        <div className="adm-mono adm-muted">{t.slug}</div>
                      </td>
                      <td>
                        <Badge tone={statusTone(t.status)}>{t.status}</Badge>
                      </td>
                      <td>{t.plan}</td>
                      <td>
                        {t.verified_domains} / {t.domains} verified
                      </td>
                      <td>
                        <Seats tenant={t} />
                      </td>
                      <td>{when(t.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )
          }
        </Loaded>
      </section>
    </>
  );
}

/** Seats used against the limit, with a warning when there is none or it is reached. */
function Seats({ tenant }: { tenant: TenantSummary }) {
  if (tenant.status === 'purged') {
    return <>—</>;
  }
  if (tenant.max_users <= 0) {
    return (
      <>
        {tenant.seats_used} <Badge tone="warn">no limit set</Badge>
      </>
    );
  }
  const full = tenant.seats_used >= tenant.max_users;
  return (
    <>
      {tenant.seats_used} of {tenant.max_users}
      {full && (
        <>
          {' '}
          <Badge tone="crit">full</Badge>
        </>
      )}
    </>
  );
}

export function NewTenant({ onCreated }: { onCreated: () => void }) {
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [domain, setDomain] = useState('');
  // Empty until the operator chooses: a tenant must not start unlimited by
  // being forgotten. 0 is unlimited, typed on purpose.
  const [seats, setSeats] = useState('');
  const [storageGb, setStorageGb] = useState('');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{ ok: boolean; text: string } | null>(null);

  const submit = (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setMessage(null);
    api('/tenants', {
      method: 'POST',
      body: {
        name,
        slug,
        domains: domain ? [domain] : [],
        max_users: Number(seats),
        max_storage_bytes: Math.round(Number(storageGb) * 1024 ** 3),
      },
    })
      .then(() => {
        setMessage({
          ok: true,
          text: `${name} onboarded. Open it to see the DNS records to publish.`,
        });
        setName('');
        setSlug('');
        setDomain('');
        setSeats('');
        setStorageGb('');
        onCreated();
      })
      .catch((e: unknown) => setMessage({ ok: false, text: describeError(e) }))
      .finally(() => setBusy(false));
  };

  return (
    <form className="adm-panel adm-form" onSubmit={submit}>
      <h3>Onboard a tenant</h3>
      <p className="adm-muted adm-small">
        Creates the tenant, its sign-in realm <code>org-&lt;slug&gt;</code>, and the domain with its
        own DKIM key. The slug cannot be changed later; seats and storage can, in the tenant's
        settings.
      </p>
      <div className="adm-fields">
        <label>
          Organisation name
          <input value={name} onChange={(e) => setName(e.target.value)} required />
        </label>
        <label>
          Slug
          <input
            value={slug}
            onChange={(e) => setSlug(e.target.value.toLowerCase())}
            pattern="[a-z0-9]([a-z0-9-]*[a-z0-9])?"
            required
          />
        </label>
        <label>
          Mail domain
          <input
            value={domain}
            onChange={(e) => setDomain(e.target.value.toLowerCase())}
            placeholder="acme.com"
          />
        </label>
        <label>
          Seats <span className="adm-muted">(0 = unlimited)</span>
          <input
            type="number"
            min={0}
            step={1}
            value={seats}
            onChange={(e) => setSeats(e.target.value)}
            placeholder="50"
            required
          />
        </label>
        <label>
          Storage, GiB <span className="adm-muted">(0 = unlimited)</span>
          <input
            type="number"
            min={0}
            step={1}
            value={storageGb}
            onChange={(e) => setStorageGb(e.target.value)}
            placeholder="500"
            required
          />
        </label>
      </div>
      <button type="submit" className="btn" disabled={busy}>
        {busy ? 'Onboarding…' : 'Onboard tenant'}
      </button>
      {message && (
        <p className={message.ok ? 'adm-ok' : 'adm-error'} role="status">
          {message.text}
        </p>
      )}
    </form>
  );
}

// ─── One tenant ──────────────────────────────────────────────────────────────

export function TenantPage({ slug, ediscovery = false }: { slug: string; ediscovery?: boolean }) {
  const [load, reload] = useLoad(
    (signal) => api<TenantDetail>(`/platform/tenants/${encodeURIComponent(slug)}`, { signal }),
    `tenant:${slug}`,
  );
  return (
    <>
      <div className="adm-head">
        <a href={href({ page: 'tenants' })} className="adm-back">
          ← Tenants
        </a>
      </div>
      <Loaded load={load}>
        {({ tenant, domains }) => (
          <>
            <div className="adm-head">
              <h2>
                {tenant.name} <Badge tone={statusTone(tenant.status)}>{tenant.status}</Badge>
              </h2>
              <span className="adm-actions">
                <a className="btn ghost" href={href({ page: 'tenant-spam', slug: tenant.slug })}>
                  Spam policy
                </a>
                <a className="btn ghost" href={href({ page: 'audit', tenant: tenant.slug })}>
                  Audit log
                </a>
              </span>
            </div>
            <dl className="adm-facts">
              <dt>Slug</dt>
              <dd className="adm-mono">{tenant.slug}</dd>
              <dt>Sign-in realm</dt>
              <dd className="adm-mono">{tenant.keycloak_realm ?? '—'}</dd>
              <dt>Plan</dt>
              <dd>{tenant.plan}</dd>
              <dt>Seats</dt>
              <dd>
                <Seats tenant={tenant} />{' '}
                {tenant.status !== 'purged' && (
                  <span className="adm-action">
                    <button
                      type="button"
                      className="btn ghost"
                      onClick={() =>
                        document
                          .getElementById('plan-and-limits')
                          ?.scrollIntoView({ block: 'start' })
                      }
                    >
                      Change limits
                    </button>
                  </span>
                )}
              </dd>
              <dt>Storage</dt>
              <dd>
                {tenant.max_storage_bytes > 0 ? (
                  `${size(tenant.max_storage_bytes)} limit`
                ) : (
                  <Badge tone="warn">no limit set</Badge>
                )}
              </dd>
              <dt>Calendar &amp; contacts</dt>
              <dd>
                <Badge tone={tenant.calendar_enabled ? 'ok' : 'warn'}>
                  {tenant.calendar_enabled ? 'included' : 'off'}
                </Badge>{' '}
                {tenant.status === 'active' && (
                  <ActionButton
                    run={async () => {
                      await api(`/platform/tenants/${encodeURIComponent(tenant.slug)}/features`, {
                        method: 'POST',
                        body: { calendar: !tenant.calendar_enabled },
                      });
                      reload();
                    }}
                  >
                    {tenant.calendar_enabled ? 'Turn off' : 'Turn on'}
                  </ActionButton>
                )}
              </dd>
              <dt>Created</dt>
              <dd>{when(tenant.created_at)}</dd>
              {tenant.status === 'suspended' && (
                <>
                  <dt>Suspended</dt>
                  <dd>{when(tenant.suspended_at)}</dd>
                  <dt>Purge allowed after</dt>
                  <dd>{when(tenant.purge_after)}</dd>
                </>
              )}
            </dl>

            <section className="adm-panel">
              <h3>Domains</h3>
              {domains.length === 0 ? (
                <p className="adm-muted">No domains.</p>
              ) : (
                <table className="adm-table">
                  <thead>
                    <tr>
                      <th>Domain</th>
                      <th>Ownership</th>
                      <th>DNS</th>
                      <th>DKIM</th>
                      <th>Last checked</th>
                    </tr>
                  </thead>
                  <tbody>
                    {domains.map((d) => (
                      <tr key={d.domain}>
                        <td>
                          <a href={href({ page: 'domain', slug: tenant.slug, domain: d.domain })}>
                            {d.domain}
                          </a>
                        </td>
                        <td>
                          <Badge tone={d.verified ? 'ok' : 'warn'}>
                            {d.verified ? 'verified' : 'unverified'}
                          </Badge>
                        </td>
                        <td>
                          <Badge tone={d.ready ? 'ok' : 'crit'}>
                            {d.ready ? 'ready' : 'action needed'}
                          </Badge>
                        </td>
                        <td className="adm-mono">
                          {d.active_selector ?? '—'}
                          {d.rotation_pending && <Badge tone="warn">rotating</Badge>}
                        </td>
                        <td>{when(d.dns_checked_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              {tenant.status === 'active' && <AddDomain slug={tenant.slug} onAdded={reload} />}
            </section>

            {tenant.status !== 'purged' && (
              <>
                <MailboxesPanel slug={tenant.slug} active={tenant.status === 'active'} />
                <AliasesPanel slug={tenant.slug} active={tenant.status === 'active'} />
                <ForwardingPolicyPanel slug={tenant.slug} active={tenant.status === 'active'} />
                <AppPasswordsPolicyPanel slug={tenant.slug} active={tenant.status === 'active'} />
                <AppSignInPolicyPanel slug={tenant.slug} active={tenant.status === 'active'} />
                <ArchiveMovePolicyPanel slug={tenant.slug} active={tenant.status === 'active'} />
                <TwoStepPolicyPanel slug={tenant.slug} active={tenant.status === 'active'} />
                <PasswordHelpPanel slug={tenant.slug} active={tenant.status === 'active'} />
                <DisclaimerPanel slug={tenant.slug} active={tenant.status === 'active'} />
                <TenantAiPanel slug={tenant.slug} active={tenant.status === 'active'} />
                {tenant.keycloak_realm && (
                  <IdentityProviderPanel slug={tenant.slug} active={tenant.status === 'active'} />
                )}
                <ScimPanel slug={tenant.slug} active={tenant.status === 'active'} />
                {tenant.keycloak_realm && (
                  <DirectorySyncPanel slug={tenant.slug} active={tenant.status === 'active'} />
                )}
                <MigrationsPanel slug={tenant.slug} active={tenant.status === 'active'} />
                <RetentionPanel slug={tenant.slug} active={tenant.status === 'active'} />
                <DlpPanel slug={tenant.slug} active={tenant.status === 'active'} />
                <TenantSettingsPanel slug={tenant.slug} editable />
              </>
            )}
            {/* The archive outlives the tenant's mailboxes, so this shows after a purge too. */}
            {ediscovery && <EDiscoveryPanel slug={tenant.slug} />}

            <Lifecycle tenant={tenant} onChanged={reload} />
          </>
        )}
      </Loaded>
    </>
  );
}

/** Attaches another domain; the tenant's own admins use it too (webmail's Domains tab). */
export function AddDomain({ slug, onAdded }: { slug: string; onAdded: () => void }) {
  const [domain, setDomain] = useState('');
  const [error, setError] = useState<string | null>(null);
  return (
    <form
      className="adm-inline"
      onSubmit={(event) => {
        event.preventDefault();
        setError(null);
        api(`/tenants/${encodeURIComponent(slug)}/domains`, { method: 'POST', body: { domain } })
          .then(() => {
            setDomain('');
            onAdded();
          })
          .catch((e: unknown) => setError(describeError(e)));
      }}
    >
      <input
        value={domain}
        onChange={(e) => setDomain(e.target.value.toLowerCase())}
        placeholder="another-domain.com"
        aria-label="Domain to add"
        required
      />
      <button type="submit" className="btn ghost">
        Add domain
      </button>
      {error && <span className="adm-error">{error}</span>}
    </form>
  );
}

function Lifecycle({ tenant, onChanged }: { tenant: TenantSummary; onChanged: () => void }) {
  const [days, setDays] = useState(30);
  const [confirm, setConfirm] = useState('');
  const path = `/platform/tenants/${encodeURIComponent(tenant.slug)}`;
  if (tenant.status === 'purged') {
    return null;
  }
  return (
    <section className="adm-panel adm-danger">
      <h3>Lifecycle</h3>
      {tenant.status === 'active' && (
        <div className="adm-inline">
          <label>
            Keep data for
            <input
              type="number"
              min={0}
              max={3650}
              value={days}
              onChange={(e) => setDays(Number(e.target.value))}
              aria-label="Retention days"
            />
            days
          </label>
          <ActionButton
            tone="danger"
            run={async () => {
              await api(`${path}/suspend`, { method: 'POST', body: { retention_days: days } });
              onChanged();
            }}
          >
            Suspend tenant
          </ActionButton>
          <p className="adm-muted adm-small">
            Stops mail and sign-ins at once. Reversible until the retention window ends.
          </p>
        </div>
      )}
      {tenant.status === 'suspended' && (
        <>
          <div className="adm-inline">
            <ActionButton
              run={async () => {
                await api(`${path}/resume`, { method: 'POST' });
                onChanged();
              }}
            >
              Resume tenant
            </ActionButton>
          </div>
          <div className="adm-inline">
            <input
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              placeholder={`type ${tenant.slug} to confirm`}
              aria-label="Type the slug to confirm the purge"
            />
            <ActionButton
              tone="danger"
              disabled={confirm !== tenant.slug}
              run={async () => {
                await api(`${path}/purge`, { method: 'POST', body: { confirm_slug: confirm } });
                setConfirm('');
                onChanged();
              }}
            >
              Purge permanently
            </ActionButton>
          </div>
          <p className="adm-muted adm-small">
            Purge deletes every mailbox record and releases the domains. It cannot be undone, and is
            refused while the retention window or a legal hold applies.
          </p>
        </>
      )}
    </section>
  );
}

// ─── One domain's DNS ────────────────────────────────────────────────────────

export function DomainPage({
  slug,
  domain,
  backHref,
}: {
  slug: string;
  domain: string;
  /** Where "back" goes; the console's tenant page unless another app embeds this. */
  backHref?: string;
}) {
  const base = `/tenants/${encodeURIComponent(slug)}/domains/${encodeURIComponent(domain)}`;
  const [load, reload] = useLoad(
    (signal) => api<DomainView>(base, { signal }),
    `domain:${slug}:${domain}`,
  );
  const [view, setView] = useState<DomainView | null>(null);
  const current = view ?? (load.state === 'ready' ? load.data : null);

  return (
    <>
      <div className="adm-head">
        <a href={backHref ?? href({ page: 'tenant', slug })} className="adm-back">
          ← {backHref ? 'Domains' : slug}
        </a>
      </div>
      <Loaded load={load}>
        {() =>
          current && (
            <>
              <div className="adm-head">
                <h2>
                  {current.domain}{' '}
                  <Badge tone={current.ready ? 'ok' : 'crit'}>
                    {current.ready ? 'DNS ready' : 'action needed'}
                  </Badge>
                </h2>
                <span className="adm-actions">
                  <ActionButton
                    run={async () =>
                      setView(await api<DomainView>(`${base}/check`, { method: 'POST' }))
                    }
                  >
                    Check DNS now
                  </ActionButton>
                  <ActionButton
                    tone="ghost"
                    disabled={current.keys.some((k) => k.status === 'pending')}
                    run={async () => {
                      setView(await api<DomainView>(`${base}/dkim/rotate`, { method: 'POST' }));
                      reload();
                    }}
                  >
                    Rotate DKIM key
                  </ActionButton>
                </span>
              </div>
              <p className="adm-muted">
                {current.verified
                  ? current.verified_at
                    ? `Verified ${when(current.verified_at)}`
                    : 'Verified'
                  : 'Not verified yet'}{' '}
                · last checked {when(current.dns_checked_at)}. Publish each record below at the
                tenant&apos;s DNS host; checks repeat automatically every few minutes until
                everything is ready.
              </p>
              <section className="adm-panel">
                <h3>Records to publish</h3>
                <table className="adm-table adm-records">
                  <thead>
                    <tr>
                      <th>Status</th>
                      <th>Record</th>
                      <th>Value</th>
                    </tr>
                  </thead>
                  <tbody>
                    {current.records.map((r) => (
                      <tr key={`${r.key}:${r.name}`}>
                        <td>
                          <Badge tone={statusTone(r.status)}>
                            {r.status === 'skipped' ? 'not checked' : r.status}
                          </Badge>
                          {!r.required && <div className="adm-muted adm-small">optional</div>}
                        </td>
                        <td>
                          <div>
                            <strong>{r.record_type}</strong>{' '}
                            <span className="adm-mono">{r.name}</span>{' '}
                            <CopyButton text={r.name} label="Copy name" />
                          </div>
                          <div className="adm-muted adm-small">{r.purpose}</div>
                          {r.note && <div className="adm-note">{r.note}</div>}
                        </td>
                        <td>
                          <code className="adm-value">{r.value}</code>
                          <CopyButton text={r.value} label="Copy value" />
                          {r.found.length > 0 && r.status !== 'ok' && (
                            <div className="adm-muted adm-small">Found: {r.found.join(' · ')}</div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </section>
              <section className="adm-panel">
                <h3>DKIM keys</h3>
                <table className="adm-table">
                  <thead>
                    <tr>
                      <th>Selector</th>
                      <th>State</th>
                      <th>Created</th>
                      <th>Signing since</th>
                      <th>Old record may be deleted after</th>
                    </tr>
                  </thead>
                  <tbody>
                    {current.keys.map((k) => (
                      <tr key={k.selector}>
                        <td className="adm-mono">{k.selector}</td>
                        <td>
                          <Badge tone={statusTone(k.status)}>{k.status}</Badge>
                        </td>
                        <td>{when(k.created_at)}</td>
                        <td>{when(k.activated_at)}</td>
                        <td>{when(k.retire_after)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </section>
              <RemoveDomain
                base={base}
                domain={current.domain}
                done={backHref ?? href({ page: 'tenant', slug })}
              />
            </>
          )
        }
      </Loaded>
    </>
  );
}

function RemoveDomain({ base, domain, done }: { base: string; domain: string; done: string }) {
  const [confirm, setConfirm] = useState('');
  return (
    <section className="adm-panel">
      <h3>Remove this domain</h3>
      <p className="adm-muted">
        Mail for {domain} stops being accepted here, and its aliases, catch-all and DKIM keys are
        deleted. Refused while any mailbox is on it, deleted ones included until they are purged, or
        while an alias on another domain delivers into it.
      </p>
      <div className="adm-inline">
        <input
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          placeholder={`type ${domain} to confirm`}
          aria-label="Type the domain to confirm removing it"
        />
        <ActionButton
          tone="danger"
          disabled={confirm.trim().toLowerCase() !== domain}
          run={async () => {
            await api(`${base}/delete`, { method: 'POST', body: { confirm_domain: domain } });
            window.location.hash = done.replace(/^#/, '');
          }}
        >
          Remove domain
        </ActionButton>
      </div>
    </section>
  );
}

// ─── Audit ───────────────────────────────────────────────────────────────────

export function AuditPage({ tenant }: { tenant?: string }) {
  const [tenantFilter, setTenantFilter] = useState(tenant ?? '');
  const [action, setAction] = useState('');
  const [applied, setApplied] = useState({ tenant: tenant ?? '', action: '' });
  const query = new URLSearchParams({ limit: '200' });
  if (applied.tenant) query.set('tenant', applied.tenant);
  if (applied.action) query.set('action', applied.action);
  const [load] = useLoad(
    (signal) => api<{ entries: AuditRecord[] }>(`/platform/audit?${query}`, { signal }),
    `audit:${query}`,
  );
  const [chain, setChain] = useState<string | null>(null);

  return (
    <>
      <div className="adm-head">
        <h2>Audit log</h2>
        <ActionButton
          tone="ghost"
          run={async () => {
            const result = await api<{ status: string; reason?: string }>('/platform/audit/verify');
            setChain(
              result.status === 'intact'
                ? 'Hash chain intact'
                : `Chain broken: ${result.reason ?? ''}`,
            );
          }}
        >
          Verify hash chain
        </ActionButton>
      </div>
      {chain && <p className={chain.startsWith('Hash') ? 'adm-ok' : 'adm-error'}>{chain}</p>}
      <form
        className="adm-inline adm-panel"
        onSubmit={(event) => {
          event.preventDefault();
          setApplied({ tenant: tenantFilter.trim(), action: action.trim() });
        }}
      >
        <input
          value={tenantFilter}
          onChange={(e) => setTenantFilter(e.target.value)}
          placeholder="tenant slug"
          aria-label="Tenant"
        />
        <input
          value={action}
          onChange={(e) => setAction(e.target.value)}
          placeholder="action, or prefix like dkim."
          aria-label="Action"
        />
        <button type="submit" className="btn ghost">
          Filter
        </button>
      </form>
      <section className="adm-panel">
        <Loaded load={load}>
          {({ entries }) =>
            entries.length === 0 ? (
              <p className="adm-muted">Nothing recorded for this filter.</p>
            ) : (
              <AuditTable entries={entries} showTenant />
            )
          }
        </Loaded>
      </section>
    </>
  );
}

/** Audit entries as a table; the tenant column only where several tenants show. */
function AuditTable({
  entries,
  showTenant = false,
}: {
  entries: readonly AuditRecord[];
  showTenant?: boolean;
}) {
  return (
    <table className="adm-table">
      <thead>
        <tr>
          <th>When</th>
          {showTenant && <th>Tenant</th>}
          <th>Action</th>
          <th>By</th>
          <th>Outcome</th>
          <th>Details</th>
        </tr>
      </thead>
      <tbody>
        {entries.map((e) => (
          <tr key={e.id}>
            <td>{when(e.timestamp)}</td>
            {showTenant && <td className="adm-mono">{e.tenant_slug ?? '—'}</td>}
            <td className="adm-mono">{e.action}</td>
            <td>{e.actor ?? '—'}</td>
            <td>
              <Badge tone={statusTone(e.outcome)}>{e.outcome}</Badge>
            </td>
            <td>
              <code className="adm-details">{JSON.stringify(e.details)}</code>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/**
 * One tenant's audit log, for its own administrators: who changed what in
 * the organisation — its admins, its identity provider, and Genworx staff.
 */
export function TenantAuditPanel({ slug }: { slug: string }) {
  const [action, setAction] = useState('');
  const [applied, setApplied] = useState('');
  const query = new URLSearchParams({ limit: '200' });
  if (applied) query.set('action', applied);
  const [load] = useLoad(
    (signal) =>
      api<{ entries: AuditRecord[] }>(`/tenants/${encodeURIComponent(slug)}/admin/audit?${query}`, {
        signal,
      }),
    `tenant-audit:${slug}:${query}`,
  );
  return (
    <section className="adm-panel">
      <h3>Activity log</h3>
      <p className="adm-muted">
        Every change to your organisation: mailboxes, sign-ins, groups, domains, policies and
        provisioning, with who made it. Entries cannot be edited or removed.
      </p>
      <form
        className="adm-inline"
        onSubmit={(event) => {
          event.preventDefault();
          setApplied(action.trim());
        }}
      >
        <input
          value={action}
          onChange={(e) => setAction(e.target.value)}
          placeholder="action, or prefix like sign_in."
          aria-label="Action"
        />
        <button type="submit" className="btn ghost">
          Filter
        </button>
      </form>
      <Loaded load={load}>
        {({ entries }) =>
          entries.length === 0 ? (
            <p className="adm-muted">Nothing recorded for this filter.</p>
          ) : (
            <AuditTable entries={entries} />
          )
        }
      </Loaded>
    </section>
  );
}
